import { FC, useCallback } from 'react';
import { useMutation } from 'react-relay';
import { useNavigate } from 'react-router';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import SaveIcon from '@mui/icons-material/Save';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from 'components/TextField';

import { useDialog } from 'components/Dialog';
import { useErrorHandler } from 'lib';

import {
  HotelListAddMutation,
  HotelListAddMutation$data,
} from '__generated__/HotelListAddMutation.graphql';
import DialogContentText from '@mui/material/DialogContentText';

type FormFields = {
  name: string;
};

type Props = {
  connectionId: string;
};

const initial = {
  name: '',
};

const HotelListAdd: FC<Props> = ({ connectionId }) => {
  const { close } = useDialog();
  const navigate = useNavigate();
  const errorHandler = useErrorHandler<HotelListAddMutation$data>();

  const [add, loading] = useMutation<HotelListAddMutation>(graphql`
    mutation HotelListAddMutation($input: CreateHotelInput!) {
      createHotel(input: $input) {
        node {
          id
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: { input },

        ...errorHandler({
          onCompleted: data => {
            navigate(`/hotels/${data.createHotel.node?.id}`);
          },
        }),

        updater: store => {
          store.get(connectionId)?.invalidateRecord();
        },
      });
    },
    [add, connectionId, errorHandler, navigate]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas viešbutis</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Čia sukurtus viešbučius, vėliau galima pridėti prie kelionės
            pasiūlymo.
          </DialogContentText>

          <TextField
            autoFocus
            name="name"
            label="Pavadinimas"
            fullWidth
            variant="standard"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={loading}
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}>
            Saugoti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default HotelListAdd;
